import { Component } from '@theme/component';

/**
 * `<blog-product-box>` — product recommendation inside a blog article.
 * Rendering is pure Liquid (snippets/blog-product-box.liquid); this component only
 * reports to PostHog whether the box was seen and used, so the blog → shop
 * funnel can be measured per article.
 *
 * Events: `blog_product_box_viewed` (once, ≥50 % visible), `blog_product_box_add_to_cart`
 * (add-to-cart form submit), `blog_product_box_clicked` (link to the product page).
 *
 * @extends {Component}
 */
class BlogProductBox extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#capture('blog_product_box_viewed');
        this.#observer?.disconnect();
      },
      { threshold: 0.5 }
    );
    this.#observer.observe(this);

    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit);
    for (const link of this.querySelectorAll('a[href*="/products/"]')) {
      link.addEventListener('click', this.#handleLinkClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.querySelector('form')?.removeEventListener('submit', this.#handleSubmit);
    for (const link of this.querySelectorAll('a[href*="/products/"]')) {
      link.removeEventListener('click', this.#handleLinkClick);
    }
  }

  #handleSubmit = () => {
    this.#capture('blog_product_box_add_to_cart');
  };

  #handleLinkClick = () => {
    this.#capture('blog_product_box_clicked');
  };

  /**
   * @param {string} name
   */
  #capture(name) {
    const { article, product, placement } = this.dataset;
    window.phCapture?.(name, { article_handle: article, product_handle: product, placement });
  }
}

if (!customElements.get('blog-product-box')) {
  customElements.define('blog-product-box', BlogProductBox);
}
